import axios from "axios";
import { toast } from "sonner";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Sensible timeout for 2G/3G: 25s is long enough for slow cold starts but
// short enough to fail before users give up and force-refresh.
export const api = axios.create({ baseURL: API, timeout: 25000 });

api.interceptors.request.use((config) => {
  const t = localStorage.getItem("sg_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Tag the request once so we know whether it's already been retried
  config.__retryCount = config.__retryCount || 0;
  return config;
});

// Retry idempotent (GET / HEAD) calls once on network failure before giving up.
// POST/PUT/DELETE are NEVER retried — payment / signup idempotency is user-owned.
const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 800;

let offlineToastId = null;
function showOfflineToast() {
  if (offlineToastId) return;
  offlineToastId = toast.error("You appear to be offline. We'll retry when you're back.", {
    duration: 6000, id: "sg-offline",
  });
}
function clearOfflineToast() {
  if (offlineToastId) {
    toast.dismiss("sg-offline");
    offlineToastId = null;
  }
}
if (typeof window !== "undefined") {
  window.addEventListener("online", clearOfflineToast);
  window.addEventListener("offline", showOfflineToast);
}

/** Retry an idempotent request once after a network failure. Returns the
 *  retried response promise, or null when this error isn't retryable. */
function maybeRetry(err) {
  const cfg = err.config || {};
  const method = (cfg.method || "get").toLowerCase();
  const isNetworkErr = !err.response;                     // ECONNABORTED / offline
  const isIdempotent = method === "get" || method === "head";
  if (!isNetworkErr || !isIdempotent || (cfg.__retryCount || 0) >= MAX_RETRIES) return null;
  cfg.__retryCount = (cfg.__retryCount || 0) + 1;
  if (typeof navigator !== "undefined" && navigator.onLine === false) showOfflineToast();
  return new Promise((r) => setTimeout(r, RETRY_DELAY_MS)).then(() => api.request(cfg));
}

/** Flatten FastAPI validation errors (422 — `detail` is an array of objects)
 *  into a plain string so callers can `toast.error(detail)` without React
 *  choking on "objects are not valid as a React child". */
function normaliseDetail(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return;
  if (Array.isArray(detail)) {
    err.response.data.detail = detail.map((d) => {
      if (!d || typeof d !== "object") return String(d);
      const where = Array.isArray(d.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
      return where ? `${where}: ${d.msg || "invalid"}` : (d.msg || "invalid");
    }).join(" · ");
  } else if (typeof detail === "object") {
    err.response.data.detail_raw = detail; // structured copy (e.g. {message, unknown_placeholders})
    try { err.response.data.detail = detail.message ? String(detail.message) : JSON.stringify(detail); }
    catch { err.response.data.detail = "Request failed"; }
  }
}

/** On 401, drop the session and bounce to login — except on public pages. */
function handleUnauthorized(err) {
  if (err?.response?.status !== 401) return;
  const path = window.location.pathname;
  const isPublic = path.startsWith("/login") || path.startsWith("/signup") || path === "/"
    || path.startsWith("/auth/") || path.startsWith("/reset-password");
  if (isPublic) return;
  localStorage.removeItem("sg_token");
  localStorage.removeItem("sg_user");
  window.location.href = "/login";
}

api.interceptors.response.use(
  (r) => { clearOfflineToast(); return r; },
  async (err) => {
    const retried = maybeRetry(err);
    if (retried) return retried;
    normaliseDetail(err);
    handleUnauthorized(err);
    return Promise.reject(err);
  }
);
